import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SkillInput from '../components/SkillInput';
import AvailabilityPicker from '../components/AvailabilityPicker';
import './Auth.css';

const STEPS = ['Account', 'Skills', 'Availability'];

const SignUp = () => {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    bio: '', location: '',
    skillsOffered: [], skillsWanted: [],
    availability: []
  });

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: typeof val === 'object' && !Array.isArray(val) ? val.target?.value ?? val : val }));

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!form.name.trim()) return setError('Name is required') || false;
      if (!form.email.trim()) return setError('Email is required') || false;
      if (!form.email.includes('@')) return setError('Invalid email address') || false;
      if (form.password.length < 6) return setError('Password must be at least 6 characters') || false;
      if (form.password !== form.confirmPassword) return setError('Passwords do not match') || false;
    }
    if (step === 1) {
      if (form.skillsOffered.length === 0) return setError('Add at least one skill you offer') || false;
      if (form.skillsWanted.length === 0) return setError('Add at least one skill you want') || false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const handleSubmit = async () => {
    setError('');
    const result = await register({
      name: form.name,
      email: form.email,
      password: form.password,
      bio: form.bio,
      location: form.location,
      skillsOffered: form.skillsOffered,
      skillsWanted: form.skillsWanted,
      availability: form.availability
    });
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="auth-logo">⇄</span>
          <h1>SkillSwap</h1>
          <p>Create your account</p>
        </div>

        <div className="auth-steps">
          {STEPS.map((label, i) => (
            <div key={i} className={`auth-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="step-dot">{i < step ? '✓' : i + 1}</div>
              <span className="step-label">{label}</span>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {step === 0 && (
          <div className="auth-fields">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input type="text" placeholder="Alex Johnson" value={form.name} onChange={set('name')} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" placeholder="alex@example.com" value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group">
              <label className="form-label">Location (optional)</label>
              <input type="text" placeholder="City, Country" value={form.location} onChange={set('location')} />
            </div>
            <div className="form-group">
              <label className="form-label">Bio (optional)</label>
              <textarea rows={2} placeholder="Tell others about yourself..." value={form.bio} onChange={set('bio')} style={{ resize: 'vertical' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input type="password" placeholder="Min. 6 characters" value={form.password} onChange={set('password')} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={set('confirmPassword')} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="auth-fields">
            <SkillInput label="Skills I Can Teach" skills={form.skillsOffered} onChange={set('skillsOffered')} placeholder="e.g. Python, Guitar, Cooking…" />
            <SkillInput label="Skills I Want to Learn" skills={form.skillsWanted} onChange={set('skillsWanted')} placeholder="e.g. Spanish, Design, Chess…" />
          </div>
        )}

        {step === 2 && (
          <div className="auth-fields">
            <AvailabilityPicker availability={form.availability} onChange={set('availability')} />
          </div>
        )}

        <div className="auth-nav">
          {step > 0 && (
            <button className="btn btn-ghost" onClick={back}>← Back</button>
          )}
          {step < STEPS.length - 1 && (
            <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={next}>
              Continue →
            </button>
          )}
          {step === STEPS.length - 1 && (
            <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={handleSubmit} disabled={loading}>
              {loading ? <><span className="loading-spinner" /> Creating…</> : 'Create Account'}
            </button>
          )}
        </div>

        <p className="auth-switch">
          Already have an account? <Link to="/signin">Sign in</Link>
        </p>
      </div>

      <div className="auth-visual">
        <div className="auth-visual-content">
          <h2>Exchange knowledge,<br />grow together.</h2>
          <p>Connect with people who have what you want to learn — and teach what you know best.</p>
          <div className="auth-visual-chips">
            {['Python → Spanish', 'Guitar → Yoga', 'Design → React', 'Chess → Cooking', 'Photography → Finance'].map((chip, i) => (
              <span key={i} className="visual-chip">{chip}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
